import Input from "@/components/common/input/Input";
import { FC, useMemo, useState } from "react";
import { PolicyNumberDetails, PolicyNumberPutPayload } from "@/models/policy";
import { policyService } from "@/services/policyService";
import Breadcrumb from "@/components/bread-crumb/BreadCrumb";
import "./PolicyNumberUpdateUtil.css";

export interface PolicyFormValues {
  fromPolicyNum: string;
  toPolicyNum: string;
  policyTermNum: string;
  policyId: string;
}

const PolicyNumberUpdateUtil: FC = () => {
  const initialFormPolicyVal: PolicyFormValues = {
    fromPolicyNum: "",
    toPolicyNum: "",
    policyTermNum: "",
    policyId: ""
  };

  const userId = useMemo(() => {
    const v = (sessionStorage.getItem("userId") ?? "").trim();
    return v.length > 0 ? v : "SYSTEM";
  }, []);

  const [searchPolicyNum, setSearchPolicyNum] = useState<string>("");
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [policyForm, setPolicyForm] = useState<PolicyFormValues>(initialFormPolicyVal);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [details, setDetails] = useState<PolicyNumberDetails | null>(null);
  const [committing, setCommitting] = useState<boolean>(false);
  const [undoEnabled, setUndoEnabled] = useState<boolean>(false);
  const [changePrepared, setChangePrepared] = useState<boolean>(false);
  const [commitNotice, setCommitNotice] = useState<string | null>(null);
  const [lastCommit, setLastCommit] = useState<{
    fromPolicyNum: string;
    toPolicyNum: string;
    policyTermNum: number;
    policyId: number;
    userId: string;
  } | null>(null);

  const digitsOnly = (v: string) => v.replace(/\D/g, "");

  const handlePolicyNumChange = (value: string) => {
    const trimmed = value.trim();
    setSearchPolicyNum(trimmed);
    setSearchError(null);
    setFormError(null);
    setCommitNotice(null);
  };

  const handleChangePolicyDetails = (value: string, id: keyof PolicyFormValues) => {
    setFormError(null);
    setCommitNotice(null);
    setChangePrepared(false);
    if (id === "policyTermNum" || id === "policyId") {
      const clean = digitsOnly(value);
      setPolicyForm(prev => ({ ...prev, [id]: clean }));
      return;
    }
    setPolicyForm(prev => ({ ...prev, [id]: value }));
  };

  const setValueForPolicyForm = (value: any) => {
    setPolicyForm({
      fromPolicyNum: String(value?.policyNum ?? "").trim(),
      toPolicyNum: "",
      policyTermNum: "",
      policyId: ""
    });
  };

  const validateRequiredFields = () => {
    const toPolicyNum = String(policyForm?.toPolicyNum ?? "").trim();
    const policyTermNumStr = String(policyForm?.policyTermNum ?? "").trim();
    const policyIdStr = String(policyForm?.policyId ?? "").trim();
    if (!toPolicyNum) return "To Policy Number is required.";
    if (!policyTermNumStr) return "Policy Term Number is required.";
    if (!policyIdStr) return "Policy Id is required.";
    if (!/^\d+$/.test(policyTermNumStr)) return "Policy Term Number must be an integer.";
    if (!/^\d+$/.test(policyIdStr)) return "Policy Id must be an integer.";
    const policyTermNum = parseInt(policyTermNumStr, 10);
    const policyId = parseInt(policyIdStr, 10);
    if (!Number.isFinite(policyTermNum) || policyTermNum <= 0) return "Policy Term Number must be greater than 0.";
    if (!Number.isFinite(policyId) || policyId <= 0) return "Policy Id must be greater than 0.";
    return null;
  };

  const handleViewDetails = async () => {
    try {
      setLoadingDetails(true);
      setSearchError(null);
      setFormError(null);
      setCommitNotice(null);
      setDetails(null);
      setPolicyForm(initialFormPolicyVal);
      setChangePrepared(false);
      setUndoEnabled(false);
      setLastCommit(null);

      const res = await policyService.getPolicyDetails(searchPolicyNum);

      if (res?.status === 204) {
        const msg =
          (res as any)?.data?.message ||
          (res as any)?.data?.errorMessage ||
          (res as any)?.data ||
          "Policy number not found.";
        setSearchError(typeof msg === "string" ? msg : "Policy number not found.");
        setDetails(null);
        return;
      }

      const data = res?.data as PolicyNumberDetails | undefined;

      if (data?.policyNum) {
        setDetails(data);
        setValueForPolicyForm(data);
      } else {
        setDetails(null);
        setSearchError("No record found for that Policy Number.");
      }
    } catch (error: any) {
      const errResponse = error?.response?.status;
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.errorMessage ||
        error?.response?.data ||
        error?.message ||
        "Something went wrong";
      if (errResponse === 204 || errResponse === 404) {
        setSearchError(typeof msg === "string" ? msg : "Policy number not found.");
      } else {
        setSearchError(typeof msg === "string" ? msg : "Something went wrong");
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  const handlePrepareChange = () => {
    setFormError(null);
    setCommitNotice(null);
    if (!details) return;
    const err = validateRequiredFields();
    if (err) {
      setFormError(err);
      setChangePrepared(false);
      return;
    }
    setChangePrepared(true);
  };

  const setupUpdatePayload = (policyOp: "I" | "U", overRide?: any) => {
    const payloadData: PolicyNumberPutPayload = {
      fromPolicyNum: String(policyForm?.fromPolicyNum ?? "").trim(),
      toPolicyNum: String(policyForm?.toPolicyNum ?? "").trim(),
      policyTermNum: parseInt(String(policyForm?.policyTermNum ?? "").trim(), 10),
      policyId: parseInt(String(policyForm?.policyId ?? "").trim(), 10),
      userId: userId.trim(),
      policyOp
    };
    return { ...payloadData, ...(overRide ?? {}) };
  };

  const setupUndoPayload = (policyOp: "I" | "U", overRide?: any) => {
    const payloadData: PolicyNumberPutPayload = {
      fromPolicyNum: lastCommit?.toPolicyNum?.trim() ?? "",
      toPolicyNum: lastCommit?.fromPolicyNum?.trim() ?? "",
      policyTermNum: lastCommit?.policyTermNum ?? 0,
      policyId: lastCommit?.policyId ?? 0,
      userId: lastCommit?.userId ?? "",
      policyOp
    };
    return { ...payloadData, ...(overRide ?? {}) };
  };

  const handleCommit = async () => {
    if (!details) return;
    setCommitting(true);
    setFormError(null);
    setCommitNotice(null);

    const err = validateRequiredFields();
    if (err) {
      setFormError(err);
      setCommitting(false);
      return;
    }

    if (!changePrepared) {
      setFormError("Please click Update Policy Number before Commit.");
      setCommitting(false);
      return;
    }

    try {
      const payload = setupUpdatePayload("I");
      const result = await policyService.updatePolicyNumber(payload);

      if (result?.status === 200 && result?.data === "Success") {
        setCommitNotice("Changes committed");
        setLastCommit({
          fromPolicyNum: payload.fromPolicyNum,
          toPolicyNum: payload.toPolicyNum,
          policyTermNum: payload.policyTermNum,
          policyId: payload.policyId,
          userId: payload.userId
        });
        setDetails(prev =>
          prev
            ? {
                ...prev,
                policyNum: payload.toPolicyNum,
                policyId: payload.policyId,
                policyTermNum: payload.policyTermNum
              }
            : prev
        );
        setPolicyForm(prev => ({
          ...prev,
          fromPolicyNum: payload.toPolicyNum,
          toPolicyNum: "",
          policyTermNum: "",
          policyId: ""
        }));
        setChangePrepared(false);
        setUndoEnabled(true);
      } else {
        const msg =
          (result as any)?.data?.message ||
          (result as any)?.data?.errorMessage ||
          (result as any)?.data ||
          "Commit failed";
        setFormError(typeof msg === "string" ? msg : "Commit failed");
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.errorMessage ||
        error?.response?.data ||
        error?.message ||
        "Commit failed";
      setFormError(typeof msg === "string" ? msg : "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const handleUndo = async () => {
    if (!lastCommit) return;
    setCommitting(true);
    setFormError(null);
    setCommitNotice(null);

    try {
      const payload = setupUndoPayload("U");
      const result = await policyService.updatePolicyNumber(payload);

      if (result?.status === 200 && result?.data === "Success") {
        setCommitNotice("Changes reverted");
        setDetails(prev =>
          prev
            ? {
                ...prev,
                policyNum: lastCommit.fromPolicyNum,
                policyId: lastCommit.policyId,
                policyTermNum: lastCommit.policyTermNum
              }
            : prev
        );
        setPolicyForm(prev => ({
          ...prev,
          fromPolicyNum: lastCommit.fromPolicyNum,
          toPolicyNum: "",
          policyTermNum: "",
          policyId: ""
        }));
        setUndoEnabled(false);
        setChangePrepared(false);
        setLastCommit(null);
      } else {
        const msg =
          (result as any)?.data?.message ||
          (result as any)?.data?.errorMessage ||
          (result as any)?.data ||
          "Undo failed";
        setFormError(typeof msg === "string" ? msg : "Undo failed");
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errorMessage ||
        err?.response?.data ||
        err?.message ||
        "Undo failed";
      setFormError(typeof msg === "string" ? msg : "Undo failed");
    } finally {
      setCommitting(false);
    }
  };

  const canPrepare = useMemo(() => !!details, [details]);

  const canCommit = useMemo(() => {
    if (!details) return false;
    if (!changePrepared) return false;
    if (committing) return false;
    return !validateRequiredFields();
  }, [details, changePrepared, committing, policyForm]);

  return (
    <>
      <article className="page-container">
        <div className="card-container">
          <Breadcrumb
            items={[
              { label: "Home", path: "/" },
              { label: "Policy Status Update", path: "/policy-status-update" }
            ]}
          />
          <div className="card-header">
            <h2>Policy Number Change Utility</h2>
            <p className="subtitle">
              Search by <strong>Policy Number</strong>, then update and confirm the change.
            </p>
          </div>
          <div className="card-section">
            <h3 className="section-title">Search Policy</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Policy Number</label>
                <Input
                  className="pn-is-input"
                  placeholder="Enter policy number"
                  value={searchPolicyNum}
                  onChange={e => handlePolicyNumChange(e.target.value)}
                />
              </div>
              <button
                className="btn primary"
                onClick={handleViewDetails}
                disabled={!searchPolicyNum?.length || loadingDetails}
              >
                View Details
              </button>
            </div>
            {searchError ? <div className="err-text">{searchError}</div> : null}
          </div>
          {details ? (
            <div className="card-section">
              <h3 className="section-title">Change Policy Number</h3>
              <div className="two-column-grid">
                <div className="info-column">
                  <div className="info-row">
                    <span className="info-label">Policy Number:</span>
                    <span className="info-value">{details?.policyNum}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Policy Id:</span>
                    <span className="info-value">{details?.policyId}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Policy Term Number:</span>
                    <span className="info-value">{details?.policyTermNum}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">File As Name:</span>
                    <span className="info-value">{details?.fileAsName}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Sub Company Code:</span>
                    <span className="info-value">{details?.subCompanyCode}</span>
                  </div>
                </div>
                <div className="form-column">
                  <div className="form-group">
                    <label>From Policy Number</label>
                    <Input className="pn-is-input" disabled={true} value={policyForm?.fromPolicyNum ?? ""} />
                  </div>
                  <div className="form-group">
                    <label>
                      To Policy Number <span className="uw-req">*</span>
                    </label>
                    <Input
                      className="pn-is-input"
                      placeholder="Enter new policy number"
                      value={policyForm?.toPolicyNum ?? ""}
                      onChange={e => handleChangePolicyDetails(e.target.value, "toPolicyNum")}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      For Policy Term Number <span className="uw-req">*</span>
                    </label>
                    <Input
                      className="pn-is-input"
                      placeholder="Enter policy term number"
                      value={policyForm?.policyTermNum ?? ""}
                      onChange={e => handleChangePolicyDetails(e.target.value, "policyTermNum")}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      For Policy Id <span className="uw-req">*</span>
                    </label>
                    <Input
                      className="pn-is-input"
                      placeholder="Enter policy id"
                      value={policyForm?.policyId ?? ""}
                      onChange={e => handleChangePolicyDetails(e.target.value, "policyId")}
                    />
                  </div>
                </div>
              </div>
              <div className="action-row">
                <button className="btn primary" disabled={!canPrepare || committing} onClick={handlePrepareChange}>
                  Update Policy Number
                </button>
                <button className="btn secondary" disabled={!canCommit} onClick={handleCommit}>
                  Commit
                </button>
                <button className="btn tertiary" disabled={!details || !undoEnabled || committing} onClick={handleUndo}>
                  Undo Change
                </button>
              </div>
              {formError ? <div className="err-text">{formError}</div> : null}
              {commitNotice ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: "10px 12px",
                    background: "#fff3cd",
                    border: "1px solid #ffeeba",
                    color: "#856404",
                    borderRadius: 6,
                    fontWeight: 600
                  }}
                >
                  {commitNotice}
                </div>
              ) : null}
              <div className="warning-text">⚠️ You need to click Commit Button for the change to take place!</div>
            </div>
          ) : null}
        </div>
      </article>
    </>
  );
};

export default PolicyNumberUpdateUtil;
``